package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.util.formatDecimal

import org.zq.astrock_ai.api.dto.KlineBarDto
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.network.StockMarketContext
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.NewsItem
import org.zq.astrock_ai.ui.research.gushi.model.NewsType
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.ui.research.gushi.model.TrendDirection
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.sqrt

fun StockProfile.toResearchStockItem(meta: StockItem? = null): StockItem {
    val priceText = tradePrice
    val pct = changePercent
        .filter { it.isDigit() || it == '.' || it == '-' }
        .toDoubleOrNull() ?: 0.0
    val direction = when {
        pct > 0.0001 -> TrendDirection.UP
        pct < -0.0001 -> TrendDirection.DOWN
        else -> TrendDirection.FLAT
    }
    return StockItem(
        name = name.ifBlank { meta?.name.orEmpty() },
        code = code,
        marketCode = fullCode.ifBlank { meta?.marketCode ?: "$code.SZ" },
        price = priceText,
        changeAmount = changeAmount,
        changePercent = changePercent,
        direction = direction,
        status = meta?.status ?: when (direction) {
            TrendDirection.UP -> "偏强"
            TrendDirection.DOWN -> "偏弱"
            TrendDirection.FLAT -> "平盘"
        },
        note = "实时行情",
        industry = industry.takeIf { it.isNotBlank() && it != "—" } ?: meta?.industry ?: "—",
        tags = meta?.tags?.takeIf { it.isNotEmpty() }
            ?: listOfNotNull(industry.takeIf { it.isNotBlank() && it != "—" }),
        sparkline = meta?.sparkline ?: defaultSparkline(direction == TrendDirection.UP),
    )
}

fun KlineBarDto.toCandle(): Candle = Candle(
    open = open.toDouble(),
    high = high.toDouble(),
    low = low.toDouble(),
    close = close.toDouble(),
    volume = volume.toDouble(),
    date = date,
    avg = avg?.toDouble(),
)

fun List<KlineBarDto>.toCandles(): List<Candle> = map { it.toCandle() }

fun StockItem.withSparklineFrom(candles: List<Candle>): StockItem {
    if (candles.size < 2) return this
    val closes = candles.takeLast(11).map { it.close }
    val min = closes.minOrNull() ?: return this
    val max = closes.maxOrNull() ?: return this
    val range = (max - min).coerceAtLeast(1e-6)
    return copy(sparkline = closes.map { ((it - min) / range).toFloat().coerceIn(0.05f, 0.98f) })
}

/** 基于 MA、MACD、RSI、KDJ、BOLL、ATR 和量价关系生成技术面综合建议。 */
fun computeTrendMetrics(stock: StockItem, candles: List<Candle>): StockTrendMetrics {
    if (candles.size < 20) return DemoData.trendMetrics(stock)

    val closes = candles.map { it.close }
    val price = closes.last()
    val previousClose = closes.getOrElse(closes.lastIndex - 1) { price }
    val changePct = if (previousClose != 0.0) (price - previousClose) / previousClose * 100.0 else 0.0
    val ma5 = closes.takeLast(5).average()
    val ma10 = closes.takeLast(10).average()
    val ma20 = closes.takeLast(20).average()
    val ma60 = closes.takeLast(minOf(60, closes.size)).average()

    val ema12 = emaSeries(closes, 12)
    val ema26 = emaSeries(closes, 26)
    val difSeries = closes.indices.map { ema12[it] - ema26[it] }
    val deaSeries = emaSeries(difSeries, 9)
    val dif = difSeries.last()
    val dea = deaSeries.last()
    val macd = (dif - dea) * 2.0
    val rsi = rsi14(closes)
    val (kdjK, kdjD, kdjJ) = kdj(candles)

    val bollStd = sqrt(closes.takeLast(20).sumOf { (it - ma20) * (it - ma20) } / 20.0)
    val bollUpper = ma20 + bollStd * 2.0
    val bollLower = ma20 - bollStd * 2.0
    val atr = atr14(candles)
    val atrPct = if (price != 0.0) atr / price * 100.0 else 0.0

    val recentVolume = candles.takeLast(5).map { it.volume }.average()
    val baselineVolumes = candles.dropLast(5).takeLast(20).map { it.volume }
    val baselineVolume = baselineVolumes.takeIf { it.isNotEmpty() }?.average() ?: recentVolume
    val volumeRatio = if (baselineVolume > 0.0) recentVolume / baselineVolume else 1.0

    val recent = candles.takeLast(20)
    val resistance = recent.maxOf { it.high }
    val support = recent.minOf { it.low }
    val prior = candles.dropLast(5).takeLast(25)
    val support2 = if (prior.isNotEmpty()) prior.minOf { it.low } else support * 0.97
    val range = (resistance - support).coerceAtLeast(1e-6)
    val posRatio = ((price - support) / range).coerceIn(0.0, 1.0)

    val bullishMa = ma5 > ma10 && ma10 > ma20
    val bearishMa = ma5 < ma10 && ma10 < ma20
    val aboveMa20 = price >= ma20
    val overbought = rsi >= 72.0 || kdjJ >= 100.0 || price > bollUpper
    val oversold = rsi <= 30.0 || kdjJ <= 0.0 || price < bollLower

    var score = 0
    score += when {
        bullishMa -> 2
        bearishMa -> -2
        else -> 0
    }
    score += if (aboveMa20) 1 else -1
    score += if (macd >= 0.0) 1 else -1
    score += if (kdjK >= kdjD) 1 else -1
    score += when {
        changePct > 0.0 && volumeRatio >= 1.05 -> 1
        changePct < 0.0 && volumeRatio >= 1.05 -> -1
        else -> 0
    }
    if (overbought) score -= 1
    if (oversold && macd > 0.0) score += 1
    score = score.coerceIn(-6, 6)

    val status = when {
        changePct >= 9.5 -> "涨停强势"
        changePct <= -9.5 -> "跌停偏弱"
        score >= 4 -> "多指标偏强"
        score >= 2 -> "震荡偏强"
        score <= -4 -> "多指标偏弱"
        score <= -2 -> "震荡偏弱"
        abs(changePct) < 0.5 && abs(price - ma20) / price < 0.01 -> "震荡整理"
        aboveMa20 -> "偏强整理"
        else -> "偏弱震荡"
    }
    val position = when {
        posRatio >= 0.88 -> "高位压力区"
        posRatio >= 0.65 -> "中高位"
        posRatio >= 0.40 -> "中位区"
        posRatio >= 0.20 -> "中低位"
        else -> "低位支撑区"
    }
    val upside = (50 + score * 6).coerceIn(20, 80)
    val weaken = 100 - upside
    val riskLevel = when {
        abs(changePct) >= 7 || atrPct >= 5.0 || overbought || posRatio >= 0.92 -> "偏高"
        atrPct <= 2.0 && score >= 2 && !overbought -> "偏低"
        else -> "中等"
    }

    val rText = formatPrice(resistance)
    val sText = formatPrice(support)
    val s2Text = formatPrice(support2)
    val chase = posRatio >= 0.85 || changePct >= 7 || overbought
    val maState = when {
        bullishMa -> "MA5>MA10>MA20，多头排列"
        bearishMa -> "MA5<MA10<MA20，空头排列"
        else -> "均线交织"
    }
    val macdState = when {
        dif >= dea && macd >= 0.0 -> "MACD偏多（红柱）"
        dif < dea && macd < 0.0 -> "MACD偏空（绿柱）"
        dif >= dea -> "MACD修复"
        else -> "MACD动能回落"
    }
    val volumeState = when {
        volumeRatio >= 1.25 && changePct > 0 -> "放量上涨"
        volumeRatio >= 1.25 && changePct < 0 -> "放量下跌"
        volumeRatio <= 0.75 -> "明显缩量"
        else -> "量能平稳"
    }
    val analysis = buildString {
        append("技术面：$maState；$macdState（DIF ${formatIndicator(dif)} / DEA ${formatIndicator(dea)}）；")
        append("RSI14 ${formatIndicator(rsi)}，KDJ ${formatIndicator(kdjK)}/${formatIndicator(kdjD)}/${formatIndicator(kdjJ)}；")
        append("BOLL ${formatPrice(bollLower)}～${formatPrice(bollUpper)}，$volumeState（量比 ${formatIndicator(volumeRatio)}）。")
        when {
            chase -> append(" 当前偏热，避免追高，等待回踩确认。")
            score >= 2 -> append(" 多项指标偏多，关注回踩支撑后的量价确认。")
            score <= -2 -> append(" 多项指标偏弱，优先防守并等待止跌信号。")
            else -> append(" 指标分歧，暂按震荡处理。")
        }
    }
    val adviceTitle = when {
        chase -> "指标偏热，等待回踩"
        score >= 4 -> "趋势偏强，回踩关注"
        score >= 2 -> "偏多但不宜追高"
        score <= -4 -> "趋势偏弱，暂避风险"
        score <= -2 -> "等待止跌企稳"
        else -> "指标分歧，保持观望"
    }

    return StockTrendMetrics(
        status = status,
        position = position,
        resistance = rText,
        support = sText,
        upsideProb = "$upside%",
        weakenProb = "$weaken%",
        riskLevel = riskLevel,
        analysis = analysis,
        adviceTitle = adviceTitle,
        positionDesc = "现价 ${formatPrice(price)}，处于近 20 日${position}；MA5=${formatPrice(ma5)}，MA10=${formatPrice(ma10)}，MA20=${formatPrice(ma20)}，MA60=${formatPrice(ma60)}。",
        buyPlan = when {
            chase -> "不追高。等待回踩 ${sText}～${formatPrice(ma20)} 后缩量企稳，或放量突破 ${rText} 后回踩不破，再小仓位确认。"
            score >= 2 -> "关注 ${sText}～${formatPrice(ma20)} 支撑区；出现缩量回踩、MACD未转弱或放量突破 ${rText} 时，再分批参与。"
            else -> "暂不急于参与。等待重新站稳 MA20（${formatPrice(ma20)}）且 MACD/KDJ 同步转强，再评估入场。"
        },
        riskHint = "技术分析不构成收益保证。ATR14=${formatPrice(atr)}（${formatIndicator(atrPct)}%）；失效参考 $s2Text，若放量跌破该位或 MA20，短线方案失效。",
    )
}

/** 将资金、换手和板块强弱加入技术面结论，形成多维度综合判断。 */
fun StockTrendMetrics.withMarketContext(context: StockMarketContext): StockTrendMetrics {
    val hasContext = context.todayMainNet != null ||
        context.recentMainNet != null ||
        context.turnoverRate != null ||
        context.strongSectors.isNotEmpty()
    if (!hasContext) return this

    val matchedSectors = context.strongSectors.filter { sector ->
        sector.name == context.industry ||
            context.concepts.any { concept ->
                sector.name.contains(concept) || concept.contains(sector.name)
            }
    }
    var adjustment = 0
    adjustment += when {
        (context.todayMainNet ?: 0.0) > 0.0 -> 3
        (context.todayMainNet ?: 0.0) < 0.0 -> -3
        else -> 0
    }
    adjustment += when {
        (context.recentMainNet ?: 0.0) > 0.0 -> 4
        (context.recentMainNet ?: 0.0) < 0.0 -> -4
        else -> 0
    }
    if (matchedSectors.isNotEmpty()) adjustment += 4
    if ((context.turnoverRate ?: 0.0) >= 20.0) adjustment -= 3

    val technicalScore = upsideProb.filter(Char::isDigit).toIntOrNull() ?: 50
    val combinedScore = (technicalScore + adjustment).coerceIn(15, 85)
    val flowConflict = technicalScore >= 56 && (context.recentMainNet ?: 0.0) < 0.0
    val highTurnover = (context.turnoverRate ?: 0.0) >= 15.0
    val contextText = buildString {
        append("资金面：")
        append("今日主力 ${formatSignedMoney(context.todayMainNet)}，")
        append("近5日 ${formatSignedMoney(context.recentMainNet)}，")
        append("换手率 ${context.turnoverRate?.let { formatIndicator(it) + "%" } ?: "--"}。")
        if (context.industry.isNotBlank()) append(" 所属${context.industry}；")
        if (context.concepts.isNotEmpty()) append("概念 ${context.concepts.take(4).joinToString("、")}；")
        if (matchedSectors.isNotEmpty()) {
            append("与强势板块 ${matchedSectors.take(3).joinToString("、") { "${it.name} ${formatSignedPercent(it.changePercent)}" }} 共振。")
        } else if (context.strongSectors.isNotEmpty()) {
            append("当前涨幅居前：${context.strongSectors.take(3).joinToString("、") { "${it.name} ${formatSignedPercent(it.changePercent)}" }}。")
        }
    }
    val combinedAdvice = when {
        highTurnover && (context.todayMainNet ?: 0.0) < 0.0 -> "高换手伴随资金流出，谨慎观望"
        flowConflict -> "技术偏强但资金流出，等待确认"
        technicalScore >= 56 && (context.recentMainNet ?: 0.0) > 0.0 && matchedSectors.isNotEmpty() ->
            "技术、资金与板块共振，回踩关注"
        technicalScore <= 44 && (context.recentMainNet ?: 0.0) < 0.0 -> "技术资金同弱，暂避风险"
        else -> adviceTitle
    }

    return copy(
        upsideProb = "$combinedScore%",
        weakenProb = "${100 - combinedScore}%",
        analysis = "$analysis $contextText",
        adviceTitle = combinedAdvice,
        positionDesc = "$positionDesc ${context.market}${context.industry.takeIf { it.isNotBlank() }?.let { "·$it" }.orEmpty()}，综合评分已计入资金与板块强弱。",
        buyPlan = "$buyPlan\n资金确认：近5日主力由流出转为流入、换手不过热，并且所属板块保持强势时，信号可靠性更高。",
        riskHint = buildString {
            append(riskHint)
            if (highTurnover) append(" 当前换手偏高，警惕分歧放大。")
            if ((context.recentMainNet ?: 0.0) < 0.0) append(" 近5日主力净流出，避免仅凭K线追涨。")
        },
    )
}

/** 将最新新闻、公告、财务、研报等事件纳入综合结论；原文仍需由用户点击核验。 */
fun StockTrendMetrics.withLatestNews(items: List<NewsItem>): StockTrendMetrics {
    val latest = items
        .distinctBy { it.title }
        .take(20)
    if (latest.isEmpty()) return this

    val encodings = latest.associateWith { org.zq.astrock_ai.quant.encodeNewsTitle(it.title) }
    fun eventScore(item: NewsItem): Int {
        val enc = encodings[item] ?: return 0
        val categoryWeight = when (item.type) {
            NewsType.ANNOUNCEMENT, NewsType.FINANCIAL -> 2.0
            NewsType.CAPITAL, NewsType.RESEARCH -> 1.0
            NewsType.NEWS -> 1.0
        }
        return (enc.polarity * enc.confidence * 4.0 * categoryWeight).toInt().coerceIn(-3, 3)
    }

    val rawScore = latest.sumOf(::eventScore)
    val newsAdjustment = rawScore.coerceIn(-8, 8)
    val severeRisks = latest.filter { encodings[it]?.hardOvernight == true }
    val positiveEvents = latest.filter { eventScore(it) > 0 }
    val negativeEvents = latest.filter { eventScore(it) < 0 }
    val newsState = when {
        severeRisks.isNotEmpty() -> "存在重大风险事件"
        newsAdjustment >= 4 -> "消息面偏正向"
        newsAdjustment <= -4 -> "消息面偏负向"
        positiveEvents.isNotEmpty() && negativeEvents.isNotEmpty() -> "消息面多空交织"
        else -> "消息面整体中性"
    }

    val baseScore = upsideProb.filter(Char::isDigit).toIntOrNull() ?: 50
    val combinedScore = (baseScore + newsAdjustment).coerceIn(10, 90)
    val focusItems = (severeRisks + latest)
        .distinctBy { it.title }
        .take(3)
    val eventSummary = focusItems.joinToString("；") { item ->
        "[${newsTypeLabel(item.type)}] ${item.title}"
    }
    val updatedAdvice = when {
        severeRisks.isNotEmpty() -> "发现重大风险消息，先核验公告"
        newsAdjustment <= -4 && baseScore >= 55 -> "技术偏强但消息承压，暂缓追涨"
        newsAdjustment <= -4 -> "消息与趋势偏弱，优先回避"
        newsAdjustment >= 4 && baseScore >= 56 -> "技术资金消息共振，回踩关注"
        else -> adviceTitle
    }

    return copy(
        upsideProb = "$combinedScore%",
        weakenProb = "${100 - combinedScore}%",
        analysis = "$analysis 最新动态分析：$newsState；已分析 ${latest.size} 条，重点：$eventSummary。",
        adviceTitle = updatedAdvice,
        positionDesc = "$positionDesc 最新消息评分已纳入综合判断（正向 ${positiveEvents.size} 条、负向 ${negativeEvents.size} 条）。",
        buyPlan = buildString {
            append(buyPlan)
            append("\n消息确认：优先查看公司公告和财务原文；消息与量价同向且持续时再执行，单条传闻不作为入场依据。")
        },
        riskHint = buildString {
            append(riskHint)
            if (severeRisks.isNotEmpty()) {
                append(" 检测到高置信利空公告：")
                append(severeRisks.take(2).joinToString("；") { it.title })
                append("，核验前不建议交易。")
            } else if (negativeEvents.isNotEmpty()) {
                append(" 近期负面动态包括：")
                append(negativeEvents.take(2).joinToString("；") { it.title })
                append("。")
            }
        },
    )
}

private fun newsTypeLabel(type: NewsType): String = when (type) {
    NewsType.NEWS -> "新闻"
    NewsType.CAPITAL -> "资金"
    NewsType.ANNOUNCEMENT -> "公告"
    NewsType.FINANCIAL -> "财务"
    NewsType.RESEARCH -> "研报"
}

private fun formatSignedMoney(value: Double?): String {
    if (value == null) return "--"
    val sign = if (value > 0.0) "+" else if (value < 0.0) "-" else ""
    val absolute = abs(value)
    val text = when {
        absolute >= 100_000_000 -> "${formatIndicator(absolute / 100_000_000)}亿"
        absolute >= 10_000 -> "${formatIndicator(absolute / 10_000)}万"
        else -> "${formatIndicator(absolute)}元"
    }
    return "$sign$text"
}

private fun formatSignedPercent(value: Double): String =
    "${if (value > 0.0) "+" else ""}${formatIndicator(value)}%"

private fun emaSeries(values: List<Double>, period: Int): List<Double> {
    if (values.isEmpty()) return emptyList()
    val factor = 2.0 / (period + 1.0)
    val result = ArrayList<Double>(values.size)
    var ema = values.first()
    values.forEach { value ->
        ema += (value - ema) * factor
        result += ema
    }
    return result
}

private fun rsi14(closes: List<Double>): Double {
    val changes = closes.zipWithNext { previous, current -> current - previous }.takeLast(14)
    if (changes.isEmpty()) return 50.0
    val gain = changes.sumOf { if (it > 0.0) it else 0.0 } / changes.size
    val loss = changes.sumOf { if (it < 0.0) -it else 0.0 } / changes.size
    if (gain == 0.0 && loss == 0.0) return 50.0
    if (loss == 0.0) return 100.0
    return 100.0 - 100.0 / (1.0 + gain / loss)
}

private fun kdj(candles: List<Candle>): Triple<Double, Double, Double> {
    var k = 50.0
    var d = 50.0
    candles.forEachIndexed { index, candle ->
        val window = candles.subList(maxOf(0, index - 8), index + 1)
        val lowest = window.minOf { it.low }
        val highest = window.maxOf { it.high }
        val rsv = if (highest > lowest) (candle.close - lowest) / (highest - lowest) * 100.0 else 50.0
        k = k * 2.0 / 3.0 + rsv / 3.0
        d = d * 2.0 / 3.0 + k / 3.0
    }
    return Triple(k, d, 3.0 * k - 2.0 * d)
}

private fun atr14(candles: List<Candle>): Double {
    if (candles.size < 2) return 0.0
    return candles.drop(1).mapIndexed { index, candle ->
        val previousClose = candles[index].close
        maxOf(
            candle.high - candle.low,
            abs(candle.high - previousClose),
            abs(candle.low - previousClose),
        )
    }.takeLast(14).average()
}

private fun formatPrice(value: Double): String {
    val scaled = round(value * 100.0) / 100.0
    return formatDecimal(scaled)
}

private fun formatIndicator(value: Double): String =
    formatDecimal(round(value * 100.0) / 100.0)

private fun defaultSparkline(isUp: Boolean): List<Float> =
    if (isUp) {
        listOf(0.30f, 0.34f, 0.32f, 0.40f, 0.45f, 0.42f, 0.52f, 0.58f, 0.55f, 0.68f, 0.74f)
    } else {
        listOf(0.72f, 0.68f, 0.70f, 0.58f, 0.52f, 0.55f, 0.44f, 0.38f, 0.42f, 0.30f, 0.26f)
    }

